import { useContext } from 'react';
import styled from 'styled-components';
import { Icon } from '@stax-core/truffle-components';
import { ProgressStore } from '../../../context';
import { history } from '../../../history';
import { ApplicationStep } from '../../routes/RouteItem.types';
import { applicationSteps } from '../../routes/private';
import { devices } from '../../../util/cssMediaQuery.util';

const ProgressWrapper = styled.aside`
  background: ${({ theme }) => theme.primaryColor};
  color: ${({ theme }) => theme.white};
  padding: 32px;
  ${devices.laptop} {
    padding: 48px;
  }
`;

const StepItem = styled.li`
  position: relative;
  list-style: none;

  > div:first-child {
    top: 2px;
  }

  > span:not(:first-child) {
    margin-left: 20px;
    padding-left: 14px;
  }

  &:after {
    content: '';
    position: absolute;
    display: block;
    top: 28px;
    bottom: 0;
    left: 10px;
    height: 100%;
    width: 1px;
    background-color: #ffffff50;
    z-index: 1;
  }
`;

const StepTitle = styled.span`
  display: block;
  font-size: 1.125rem;
  font-weight: ${({ isActive }: any) => (isActive ? 700 : 400)};
`;

const StepSubSection = styled.span`
  display: block;
  font-size: 0.875rem;
`;

const StepIndicator = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 20px;
  width: 20px;
  border-radius: 100%;
  box-shadow: inset 0 0 0 2px #ffffff50;
  font-size: 1.25rem;

  &[data-validationstate='valid'] {
    background: ${({ theme }) => theme.colors.status.green['500'].hex};
    box-shadow: none; // font awesome icon provides the border in this state
  }

  &[data-validationstate='invalid'] {
    background: ${({ theme }) => theme.colors.status.red['600'].hex};
    box-shadow: none; // font awesome icon provides the border in this state
  }

  &[data-active='true'] {
    background: ${({ theme }) => theme.colors.status.blue['500'].hex};
    box-shadow: none; // font awesome icon provides the border in this state
  }
`;

const DesktopProgress = styled.div`
  margin-top: 40px;

  ${StepItem} + ${StepItem} {
    margin-top: 32px;
  }

  ${StepItem}:last-child:after {
    display: none;
  }
`;

type ValidationState = 'valid' | 'invalid' | 'incomplete';

const Step = (props: {
  stepConfig: ApplicationStep;
  isActive: boolean;
  validationState: ValidationState;
}) => {
  const { stepConfig, validationState, isActive } = props;
  return (
    <StepItem>
      <StepIndicator
        data-validationstate={validationState}
        data-active={isActive}
      >
        {validationState === 'valid' ? (
          <Icon icon={['far', 'check-circle']} />
        ) : null}
        {validationState === 'invalid' ? (
          <Icon icon={['far', 'times-circle']} />
        ) : null}
      </StepIndicator>
      <StepTitle
        {...({ isActive } as any)}
        onclick={() => {
          history.push('/#' + stepConfig.subSections?.[0].path);
        }}
      >
        {stepConfig.title}
      </StepTitle>
      {stepConfig.subSections
        ? stepConfig.subSections.map((subStep, index) =>
            subStep?.hideFromProgressUi ? (
              <></>
            ) : (
              <StepSubSection key={index}>{subStep?.title}</StepSubSection>
            ),
          )
        : null}
    </StepItem>
  );
};

/**
 * Progress steps sidebar - shows a list of all steps in the application flow
 * with progress indicators for each step of invalid, in-progress, or complete.
 */
export const Progress = () => {
  const {
    state: { currentStepPath, steps },
  } = useContext(ProgressStore);

  const renderSteps = () => {
    const stepKeys = Object.keys(steps);
    const allStepNames = Object.keys(
      applicationSteps,
    ) as (keyof typeof applicationSteps)[];

    return allStepNames.map((stepName, index) => {
      const step = applicationSteps[stepName] as ApplicationStep;
      const isCurrentStep =
        step.subSections?.find(sec => sec.path === currentStepPath) != null;
      // if this route has sub-sections look for step keys in the form 'key.#'
      // sub-sections without titles do not appear in the UI and don't count toward completing that step
      const expectedStepCount =
        step.subSections?.filter(k => k.title?.length > 0).length ?? 1;
      // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
      const searchId = step.subSections?.length! > 0 ? step.id + '.' : step.id;
      const matchingSteps = stepKeys.filter(k =>
        k.startsWith(searchId),
      ) as (keyof typeof steps)[];
      let allValid =
        matchingSteps.filter(k => steps[k].validationState === 'valid')
          .length === expectedStepCount;
      const allErrors =
        matchingSteps.filter(k => steps[k].validationState === 'invalid')
          .length === expectedStepCount;
      // Final Submit step is shown as 'done' as soon as you get to the pages in that section
      if (
        stepName === 'submitStep' &&
        (currentStepPath === '/app/review' || currentStepPath === '/app/submit')
      ) {
        allValid = true;
      }
      return (
        <Step
          key={index}
          stepConfig={step}
          isActive={isCurrentStep}
          validationState={
            allValid ? 'valid' : allErrors ? 'invalid' : 'incomplete'
          }
        />
      );
    });
  };

  return (
    <ProgressWrapper>
      <h3>Merchant Registration</h3>
      <DesktopProgress>{renderSteps()}</DesktopProgress>
    </ProgressWrapper>
  );
};
